import { createApi } from '@reduxjs/toolkit/query/react';
import { appPortalBaseQuery } from '@/features/baseQuery.ts';
import type {
  AddFundReferenceDataRequest,
  AddReportingFundReferenceDataRequest,
  AddShareClassReferenceDataRequest,
  BackendFundReferenceDto,
  BackendReportingFundReferenceDto,
  BackendShareClassReferenceDto,
  DeactivateFundReferenceDataRequest,
  DeactivateShareClassReferenceDataRequest,
  GetFundReferenceDataRequest,
  GetShareClassReferenceDataRequest,
  ReactivateFundReferenceDataRequest,
  ReactivateShareClassReferenceDataRequest,
  ReferenceDataItem,
  ReportingFundReferenceItem,
  UpdateFundReferenceDataRequest,
  UpdateReportingFundReferenceDataRequest,
  UpdateShareClassReferenceDataRequest,
} from '@/features/reference-data/reference-data.def.ts';
import {
  mapFundReferenceItem,
  mapReportingFundReferenceItem,
  mapShareClassReferenceItem,
} from '@/features/reference-data/reference-data.mappers.ts';
import { invalidateAnalyticsOnSuccess } from '@/features/analytics/invalidate-analytics.ts';

/**
 * RTK Query API for the Fund and Share Class reference-data screens used by the Finance
 * team. Talks to `FundReferenceDataController` / `ShareClassReferenceDataController`.
 *
 * Unlike `fundManagementApi` (the read-only Fund performance Dashboard), every mutation
 * here identifies records by `id` (a Guid), never by display name.
 *
 * Every mutation also drops cached analytics reads (`invalidateAnalyticsOnSuccess`): fund and
 * share-class display names appear on every metrics page, and the server reads them fresh per
 * request, so the SPA cache is the only place a stale name could survive.
 */
export const referenceDataApi = createApi({
  reducerPath: 'referenceDataApi',
  baseQuery: appPortalBaseQuery(),
  tagTypes: ['FundReferenceData', 'ShareClassReferenceData', 'ReportingFundReferenceData'],
  endpoints: (builder) => ({
    getFundReferenceData: builder.query<ReferenceDataItem[], GetFundReferenceDataRequest | void>({
      query: (request) => ({
        url: '/fund-reference-data',
        method: 'GET',
        params: { includeInactive: request?.includeInactive ?? false },
      }),
      transformResponse: (response: BackendFundReferenceDto[]) =>
        response.map(mapFundReferenceItem),
      providesTags: ['FundReferenceData'],
    }),
    addFundReferenceData: builder.mutation<ReferenceDataItem, AddFundReferenceDataRequest>({
      query: (body) => ({
        url: '/fund-reference-data',
        method: 'POST',
        body,
      }),
      transformResponse: (response: BackendFundReferenceDto) => mapFundReferenceItem(response),
      invalidatesTags: ['FundReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    updateFundReferenceData: builder.mutation<ReferenceDataItem, UpdateFundReferenceDataRequest>({
      query: ({ fundId, ...body }) => ({
        url: `/fund-reference-data/${fundId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: BackendFundReferenceDto) => mapFundReferenceItem(response),
      invalidatesTags: ['FundReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    deactivateFundReferenceData: builder.mutation<void, DeactivateFundReferenceDataRequest>({
      query: ({ fundId }) => ({
        url: `/fund-reference-data/${fundId}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['FundReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    reactivateFundReferenceData: builder.mutation<void, ReactivateFundReferenceDataRequest>({
      query: ({ fundId }) => ({
        url: `/fund-reference-data/${fundId}/reactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['FundReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),

    getReportingFundReferenceData: builder.query<ReportingFundReferenceItem[], void>({
      query: () => ({
        url: '/reporting-fund-reference-data',
        method: 'GET',
      }),
      transformResponse: (response: BackendReportingFundReferenceDto[]) =>
        response.map(mapReportingFundReferenceItem),
      providesTags: ['ReportingFundReferenceData'],
    }),
    addReportingFundReferenceData: builder.mutation<
      ReportingFundReferenceItem,
      AddReportingFundReferenceDataRequest
    >({
      query: (body) => ({
        url: '/reporting-fund-reference-data',
        method: 'POST',
        body,
      }),
      transformResponse: (response: BackendReportingFundReferenceDto) =>
        mapReportingFundReferenceItem(response),
      invalidatesTags: ['ReportingFundReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    updateReportingFundReferenceData: builder.mutation<
      ReportingFundReferenceItem,
      UpdateReportingFundReferenceDataRequest
    >({
      query: ({ reportingFundId, ...body }) => ({
        url: `/reporting-fund-reference-data/${reportingFundId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: BackendReportingFundReferenceDto) =>
        mapReportingFundReferenceItem(response),
      invalidatesTags: ['ReportingFundReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    getShareClassReferenceData: builder.query<
      ReferenceDataItem[],
      GetShareClassReferenceDataRequest | void
    >({
      query: (request) => ({
        url: '/share-class-reference-data',
        method: 'GET',
        params: { includeInactive: request?.includeInactive ?? false },
      }),
      transformResponse: (response: BackendShareClassReferenceDto[]) =>
        response.map(mapShareClassReferenceItem),
      providesTags: ['ShareClassReferenceData'],
    }),
    addShareClassReferenceData: builder.mutation<
      ReferenceDataItem,
      AddShareClassReferenceDataRequest
    >({
      query: (body) => ({
        url: '/share-class-reference-data',
        method: 'POST',
        body,
      }),
      transformResponse: (response: BackendShareClassReferenceDto) =>
        mapShareClassReferenceItem(response),
      invalidatesTags: ['ShareClassReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    updateShareClassReferenceData: builder.mutation<
      ReferenceDataItem,
      UpdateShareClassReferenceDataRequest
    >({
      query: ({ shareClassId, ...body }) => ({
        url: `/share-class-reference-data/${shareClassId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: BackendShareClassReferenceDto) =>
        mapShareClassReferenceItem(response),
      invalidatesTags: ['ShareClassReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    deactivateShareClassReferenceData: builder.mutation<
      void,
      DeactivateShareClassReferenceDataRequest
    >({
      query: ({ shareClassId }) => ({
        url: `/share-class-reference-data/${shareClassId}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['ShareClassReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
    reactivateShareClassReferenceData: builder.mutation<
      void,
      ReactivateShareClassReferenceDataRequest
    >({
      query: ({ shareClassId }) => ({
        url: `/share-class-reference-data/${shareClassId}/reactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['ShareClassReferenceData'],
      onQueryStarted: invalidateAnalyticsOnSuccess,
    }),
  }),
});

export const {
  useGetFundReferenceDataQuery,
  useAddFundReferenceDataMutation,
  useUpdateFundReferenceDataMutation,
  useDeactivateFundReferenceDataMutation,
  useReactivateFundReferenceDataMutation,
  useGetShareClassReferenceDataQuery,
  useAddShareClassReferenceDataMutation,
  useUpdateShareClassReferenceDataMutation,
  useDeactivateShareClassReferenceDataMutation,
  useReactivateShareClassReferenceDataMutation,
  useGetReportingFundReferenceDataQuery,
  useAddReportingFundReferenceDataMutation,
  useUpdateReportingFundReferenceDataMutation,
} = referenceDataApi;
